import { RootState } from '@/lib/redux/store';
import { createSelector } from '@reduxjs/toolkit';

export const selectJobEditorsSlice = (state: RootState) => state.jobEditors;

export const selectJobEditor = (jobId: string) => (state: RootState) =>
  state.jobEditors[jobId];

export const selectJobMode = (jobId: string) =>
  createSelector(selectJobEditor(jobId), (editor) => editor?.mode || 'new');

export const selectOriginalJobData = (jobId: string) =>
  createSelector(selectJobEditor(jobId), (editor) => editor?.originalJobData);

export const selectIsDirty = (jobId: string) =>
  createSelector(selectJobEditor(jobId), (editor) => editor?.isDirty || false);

export const selectIdentityId = (jobId: string) =>
  createSelector(selectJobEditor(jobId), (editor) => editor?.identity_id || '');

export const selectJobType = (jobId: string) =>
  createSelector(selectJobEditor(jobId), (editor) => editor?.jobType);

export const selectJobName = (jobId: string) =>
  createSelector(selectJobEditor(jobId), (editor) => editor?.jobName || '');

export const selectJobArea = (jobId: string) =>
  createSelector(selectJobEditor(jobId), (editor) => editor?.jobArea || '');

export const selectDidNpaSubset = (jobId: string) =>
  createSelector(selectJobEditor(jobId), (editor) => editor?.didNpaSubset ?? []);

export const selectBillingId = (jobId: string) =>
  createSelector(selectJobEditor(jobId), (editor) => editor?.billingId || '');

export const selectIsAiEnabled = (jobId: string) =>
  createSelector(selectJobEditor(jobId), (editor) => editor?.isAiEnabled || false);

export const selectAiAutoOptOutThreshold = (jobId: string) =>
  createSelector(selectJobEditor(jobId), (editor) => editor?.aiAutoOptOutThreshold);

export const selectUsesRateplan = (jobId: string) =>
  createSelector(selectJobEditor(jobId), (editor) => editor?.usesRateplan || false);

export const selectActiveEntrySectionId = (jobId: string) =>
  createSelector(selectJobEditor(jobId), (editor) => editor?.activeEntrySectionId);

export const selectIsShowingFloatingEmoji = (jobId: string) =>
  createSelector(
    selectJobEditor(jobId),
    (editor) => editor?.isShowingFloatingEmoji || false
  );

export const selectIsShowingFloatingAttributes = (jobId: string) =>
  createSelector(
    selectJobEditor(jobId),
    (editor) => editor?.isShowingFloatingAttributes || false
  );

export const selectIsShowingMediaManager = (jobId: string) =>
  createSelector(
    selectJobEditor(jobId),
    (editor) => editor?.isShowingMediaManager || false
  );

export const selectAssignedContactLists = (jobId: string) =>
  createSelector(selectJobEditor(jobId), (editor) => editor?.assignedContactLists || []);

export const selectAssignedAgents = (jobId: string) =>
  createSelector(selectJobEditor(jobId), (editor) => editor?.assignedAgents || []);

export const selectAgentToAutoAssign = (jobId: string) =>
  createSelector(selectJobEditor(jobId), (editor) => editor?.autoAssignAgent);

export const selectIsNewAgentCreated = (jobId: string) =>
  createSelector(selectJobEditor(jobId), (editor) => editor?.isNewAgentCreated || false);

export const selectIsNoAgentResponder = (jobId: string) =>
  createSelector(selectJobEditor(jobId), (editor) => {
    const assignedAgents = editor?.assignedAgents;
    return assignedAgents?.length ? assignedAgents.every((a) => !a.isResponder) : false;
  });

export const selectAssignedSuppressionLists = (jobId: string) =>
  createSelector(
    selectJobEditor(jobId),
    (editor) => editor?.assignedSuppressionLists || []
  );

export const selectIsNewSuppressionListCreated = (jobId: string) =>
  createSelector(
    selectJobEditor(jobId),
    (editor) => editor?.isNewSuppressionListCreated || false
  );

export const selectQuestions = (jobId: string) =>
  createSelector(selectJobEditor(jobId), (editor) => editor?.questions || []);

export const selectTrackedLinks = (jobId: string) =>
  createSelector(selectJobEditor(jobId), (editor) => editor?.trackedLinks || []);

export const selectLinkToAddToInitialMessage = (jobId: string) =>
  createSelector(selectJobEditor(jobId), (editor) => editor?.linkToAddToInitialMessage);

export const selectInitialMessageMedia = (jobId: string) =>
  createSelector(selectJobEditor(jobId), (editor) => editor?.initialMessageMedia);

export const selectInitialMessageText = (jobId: string) =>
  createSelector(selectJobEditor(jobId), (editor) => editor?.initialMessageText || '');

export const selectInitialMessageHtml = (jobId: string) =>
  createSelector(selectJobEditor(jobId), (editor) => editor?.initialMessageHtml || '');

export const selectInitialMessageOptOutText = (jobId: string) =>
  createSelector(selectJobEditor(jobId), (editor) => editor?.initialMessageOptOutText);

export const selectInitialMessageMaxLength = (jobId: string) =>
  createSelector(selectJobEditor(jobId), (editor) => editor?.initialMessageMaxLength);

export const selectInitialMessageValidationMessages = (jobId: string) =>
  createSelector(
    selectJobEditor(jobId),
    (editor) => editor?.initialMessageValidationMessages || []
  );

export const selectReplyTemplates = (jobId: string) =>
  createSelector(selectJobEditor(jobId), (editor) => {
    const originalJobData = editor?.originalJobData;
    if (!originalJobData?.templates) return [];

    // Return all templates except the default one (which is used for initial message)
    return originalJobData.templates.filter((template) => !template.is_default);
  });

// Legacy selectors for backwards compatibility (these will select from the first available job editor or return defaults)
const selectFirstJobEditor = createSelector(selectJobEditorsSlice, (editors) => {
  const firstJobId = Object.keys(editors)[0];
  return firstJobId ? editors[firstJobId] : undefined;
});

// These are for backwards compatibility with existing components that don't pass jobId yet
export const selectNewJobSlice = selectJobEditorsSlice;

// Legacy selectors that work with the first available job editor - maintain original names for backwards compatibility
export const selectIdentityIdLegacy = createSelector(
  selectFirstJobEditor,
  (editor) => editor?.identity_id || ''
);

export const selectJobTypeLegacy = createSelector(
  selectFirstJobEditor,
  (editor) => editor?.jobType
);

export const selectJobNameLegacy = createSelector(
  selectFirstJobEditor,
  (editor) => editor?.jobName || ''
);

export const selectJobAreaLegacy = createSelector(
  selectFirstJobEditor,
  (editor) => editor?.jobArea || ''
);

export const selectIsAiEnabledLegacy = createSelector(
  selectFirstJobEditor,
  (editor) => editor?.isAiEnabled || false
);

export const selectAiAutoOptOutThresholdLegacy = createSelector(
  selectFirstJobEditor,
  (editor) => editor?.aiAutoOptOutThreshold
);

export const selectActiveEntrySectionIdLegacy = createSelector(
  selectFirstJobEditor,
  (editor) => editor?.activeEntrySectionId
);

export const selectIsShowingFloatingEmojiLegacy = createSelector(
  selectFirstJobEditor,
  (editor) => editor?.isShowingFloatingEmoji || false
);

export const selectIsShowingFloatingAttributesLegacy = createSelector(
  selectFirstJobEditor,
  (editor) => editor?.isShowingFloatingAttributes || false
);

export const selectIsShowingMediaManagerLegacy = createSelector(
  selectFirstJobEditor,
  (editor) => editor?.isShowingMediaManager || false
);

export const selectAssignedContactListsLegacy = createSelector(
  selectFirstJobEditor,
  (editor) => editor?.assignedContactLists || []
);

export const selectAssignedAgentsLegacy = createSelector(
  selectFirstJobEditor,
  (editor) => editor?.assignedAgents || []
);

export const selectAgentToAutoAssignLegacy = createSelector(
  selectFirstJobEditor,
  (editor) => editor?.autoAssignAgent
);

export const selectIsNewAgentCreatedLegacy = createSelector(
  selectFirstJobEditor,
  (editor) => editor?.isNewAgentCreated || false
);

export const selectIsNoAgentResponderLegacy = createSelector(
  selectFirstJobEditor,
  (editor) => {
    const assignedAgents = editor?.assignedAgents;
    return assignedAgents?.length ? assignedAgents.every((a) => !a.isResponder) : false;
  }
);

export const selectAssignedSuppressionListsLegacy = createSelector(
  selectFirstJobEditor,
  (editor) => editor?.assignedSuppressionLists || []
);

export const selectIsNewSuppressionListCreatedLegacy = createSelector(
  selectFirstJobEditor,
  (editor) => editor?.isNewSuppressionListCreated || false
);

export const selectQuestionsLegacy = createSelector(
  selectFirstJobEditor,
  (editor) => editor?.questions || []
);

export const selectTrackedLinksLegacy = createSelector(
  selectFirstJobEditor,
  (editor) => editor?.trackedLinks || []
);

export const selectLinkToAddToInitialMessageLegacy = createSelector(
  selectFirstJobEditor,
  (editor) => editor?.linkToAddToInitialMessage
);

export const selectInitialMessageMediaLegacy = createSelector(
  selectFirstJobEditor,
  (editor) => editor?.initialMessageMedia
);

export const selectInitialMessageTextLegacy = createSelector(
  selectFirstJobEditor,
  (editor) => editor?.initialMessageText || ''
);

export const selectInitialMessageHtmlLegacy = createSelector(
  selectFirstJobEditor,
  (editor) => editor?.initialMessageHtml || ''
);

export const selectInitialMessageOptOutTextLegacy = createSelector(
  selectFirstJobEditor,
  (editor) => editor?.initialMessageOptOutText
);

export const selectInitialMessageMaxLengthLegacy = createSelector(
  selectFirstJobEditor,
  (editor) => editor?.initialMessageMaxLength
);

export const selectInitialMessageValidationMessagesLegacy = createSelector(
  selectFirstJobEditor,
  (editor) => editor?.initialMessageValidationMessages || []
);

// Re-export with original names for backwards compatibility during Phase 1
export {
  selectActiveEntrySectionIdLegacy as selectActiveEntrySectionIdOriginal,
  selectAgentToAutoAssignLegacy as selectAgentToAutoAssignOriginal,
  selectAiAutoOptOutThresholdLegacy as selectAiAutoOptOutThresholdOriginal,
  selectAssignedAgentsLegacy as selectAssignedAgentsOriginal,
  selectAssignedContactListsLegacy as selectAssignedContactListsOriginal,
  selectAssignedSuppressionListsLegacy as selectAssignedSuppressionListsOriginal,
  selectIdentityIdLegacy as selectIdentityIdOriginal,
  selectInitialMessageHtmlLegacy as selectInitialMessageHtmlOriginal,
  selectInitialMessageMaxLengthLegacy as selectInitialMessageMaxLengthOriginal,
  selectInitialMessageMediaLegacy as selectInitialMessageMediaOriginal,
  selectInitialMessageOptOutTextLegacy as selectInitialMessageOptOutTextOriginal,
  selectInitialMessageTextLegacy as selectInitialMessageTextOriginal,
  selectInitialMessageValidationMessagesLegacy as selectInitialMessageValidationMessagesOriginal,
  selectIsAiEnabledLegacy as selectIsAiEnabledOriginal,
  selectIsNewAgentCreatedLegacy as selectIsNewAgentCreatedOriginal,
  selectIsNewSuppressionListCreatedLegacy as selectIsNewSuppressionListCreatedOriginal,
  selectIsNoAgentResponderLegacy as selectIsNoAgentResponderOriginal,
  selectIsShowingFloatingAttributesLegacy as selectIsShowingFloatingAttributesOriginal,
  selectIsShowingFloatingEmojiLegacy as selectIsShowingFloatingEmojiOriginal,
  selectIsShowingMediaManagerLegacy as selectIsShowingMediaManagerOriginal,
  selectJobAreaLegacy as selectJobAreaOriginal,
  selectJobNameLegacy as selectJobNameOriginal,
  selectJobTypeLegacy as selectJobTypeOriginal,
  selectLinkToAddToInitialMessageLegacy as selectLinkToAddToInitialMessageOriginal,
  selectQuestionsLegacy as selectQuestionsOriginal,
  selectTrackedLinksLegacy as selectTrackedLinksOriginal,
};

// Validation selectors
export const selectValidationState = (jobId: string) =>
  createSelector(
    selectJobEditor(jobId),
    (editor) =>
      editor?.validationState || {
        isValid: false,
        errors: {},
        isValidating: false,
      }
  );

export const selectIsValid = (jobId: string) =>
  createSelector(
    selectValidationState(jobId),
    (validationState) => validationState.isValid
  );

export const selectValidationErrors = (jobId: string) =>
  createSelector(
    selectValidationState(jobId),
    (validationState) => validationState.errors
  );

export const selectIsValidating = (jobId: string) =>
  createSelector(
    selectValidationState(jobId),
    (validationState) => validationState.isValidating
  );

export const selectCanSave = (jobId: string) =>
  createSelector(
    [selectIsValid(jobId), selectIsValidating(jobId), selectIsDirty(jobId)],
    (isValid, isValidating, isDirty) => isValid && !isValidating && isDirty
  );
